import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { usersService } from '../../services/firestoreService';
import { deleteUser as firebaseDeleteUser } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { activityLogService } from '../../services/activityLogService';
import ThemeSelector from './ThemeSelector';
import './UserSettings.css';

const UserSettings = () => {
  const { user, signOut } = useUser();
  const navigate = useNavigate();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadUserPreferences = async () => {
    try {
      const userData = await usersService.getUser(user.id);
      if (userData?.preferences) {
        setNotificationsEnabled(userData.preferences.notificationsEnabled || false);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  useEffect(() => {
    if (user) {
      // Load user preferences
      loadUserPreferences();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleNotifications = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      const newValue = !notificationsEnabled;
      await usersService.updateUser(user.id, {
        notificationsEnabled: newValue
      });
      // Log settings update
      await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.SETTINGS_UPDATE, {
        notificationsEnabled: newValue
      });
      setNotificationsEnabled(newValue);
      alert(`Notifications ${newValue ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error updating notifications:', error);
      alert('Error updating notification settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    if (!user) return;

    const confirmText = prompt(
      'This action cannot be undone. Type "DELETE" to confirm account deletion:'
    );

    if (confirmText !== 'DELETE') {
      alert('Account deletion cancelled.');
      setDeleteConfirm(false);
      return;
    }

    setDeleteLoading(true);
    try {
      // Log account deletion before deleting
      await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.DELETE_ACCOUNT, {
        email: user.email
      });
      
      // Delete user document from Firestore
      await usersService.deleteUser(user.id);
      
      // Delete Firebase Auth account
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        await firebaseDeleteUser(firebaseUser);
      }

      // Sign out and navigate to home
      await signOut();
      navigate('/');
      alert('Your account has been deleted successfully.');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(`Error deleting account: ${error.message || 'Please try again.'}`);
      setDeleteLoading(false);
      setDeleteConfirm(false);
    }
  };

  if (!user) {
    return <div>Please sign in to access settings.</div>;
  }

  return (
    <div className="user-settings">
      <h1>⚙️ Account Settings</h1>
      
      <div className="settings-section">
        <h2>🎨 Theme</h2>
        <ThemeSelector />
      </div>

      <div className="settings-section">
        <h2>Notifications</h2>
        <div className="setting-item">
          <label className="toggle-label">
            <span>Enable Notifications</span>
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationsEnabled}
                onChange={handleToggleNotifications}
                disabled={saving}
              />
              <span className="toggle-slider"></span>
            </div>
          </label>
          <p className="setting-description">
            Receive notifications about your sessions, homework, and important updates.
          </p>
        </div>
      </div>

      <div className="settings-section danger-section">
        <h2>Danger Zone</h2>
        <div className="setting-item">
          <p className="danger-warning">
            ⚠️ Deleting your account will permanently remove all your data, including:
          </p>
          <ul className="delete-list">
            <li>Your profile information</li>
            <li>All session registrations</li>
            <li>All homework submissions</li>
            <li>All attendance records</li>
          </ul>
          <p className="danger-warning">This action cannot be undone.</p>
          <button
            className={`delete-account-btn ${deleteConfirm ? 'confirm' : ''}`}
            onClick={handleDeleteAccount}
            disabled={deleteLoading}
          >
            {deleteLoading
              ? 'Deleting...'
              : deleteConfirm
              ? '⚠️ Click again to confirm deletion'
              : '🗑️ Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;

